import type { JSX } from 'react';
import { Box, SectionHeader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type {
    IOutclinicDashboardProcess,
    IStepCount,
    IStepGroup,
    OutclinicDashboardServiceCode,
} from '@common/interfaces/api/outclinicDashboardApi';
import type { IServicesGetAllItem } from '@common/interfaces/api/servicesApi';
import { ServiceCode } from '@common/enums';
import { OutclinicDashboardStepCardFiltersTop } from '@frontend/components/pages/outclinicDashboard/outclinicDashboardStepCardFilters/OutclinicDashboardStepCardFiltersTop';
import {
    getStepCounts,
    getStepGroups,
    listProcesses,
    snoozeProcess,
} from '@frontend/services/api/outclinicDashboardApiService';
import { findByCode } from '@frontend/services/api/servicesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { OutclinicDashboardLoader } from './OutclinicDashboardLoader';
import { OutclinicDashboardStepFiltersSide } from './outclinicDashboardStepFilters/OutclinicDashboardStepFiltersSide';
import { OutclinicDashboardTable } from './OutclinicDashboardTable/OutclinicDashboardTable';

interface IOutclinicDashboardProps {
    serviceCode: ServiceCode;
}

export function OutclinicDashboard(props: Readonly<IOutclinicDashboardProps>): JSX.Element {
    const abortSignal = useAbortSignal();
    const [stepGroups, setStepGroups] = useState<IStepGroup[]>([]);
    const [stepCounts, setStepCounts] = useState<IStepCount[]>([]);
    const [hideSnoozed, setHideSnoozed] = useState(true);
    const [warningsOnly, setWarningsOnly] = useState(true);
    const [filteredDashboardStepIdentifier, setFilteredDashboardStepIdentifier] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [processes, setProcesses] = useState<IOutclinicDashboardProcess[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [service, setService] = useState<IServicesGetAllItem>();

    useEffect(() => {
        loadService();
    }, []);

    useEffect(() => {
        loadStepGroups();
        loadStepCounts();
    }, [service, hideSnoozed]);

    useEffect(() => {
        document.querySelector('#table')?.scrollIntoView();
    }, [page, pageSize, filteredDashboardStepIdentifier]);

    useEffect(() => {
        loadProcesses();
    }, [hideSnoozed, filteredDashboardStepIdentifier, stepCounts, page, pageSize, warningsOnly]);

    useEffect(() => {
        setPage(1);
    }, [hideSnoozed, filteredDashboardStepIdentifier, warningsOnly]);

    if (!service || stepCounts.length === 0 || stepGroups.length === 0) {
        return <OutclinicDashboardLoader />;
    }

    return (
        <>
            {props.serviceCode === ServiceCode.OSAS && (
                <>
                    <OutclinicDashboardStepCardFiltersTop
                        activeDashboardStepIdentifier={filteredDashboardStepIdentifier}
                        counts={stepCounts}
                        groups={stepGroups}
                        onChange={handleFilterChange}
                        serviceCode={service.code as OutclinicDashboardServiceCode}
                    />
                    <SectionHeader
                        subtitle={
                            findDashboardNameByStepIdentifier(filteredDashboardStepIdentifier) ?? '[Geen filter actief]'
                        }
                        title="PID's in proces"
                    />
                </>
            )}

            <Box
                display="flex"
                flexDirection={['column', null, 'row']}
                gap={8}
                minWidth={0}
                sx={props.serviceCode !== ServiceCode.OSAS && { mt: 10 }}
                width="100%"
            >
                {props.serviceCode !== ServiceCode.OSAS && (
                    <OutclinicDashboardStepFiltersSide
                        activeDashboardStepIdentifier={filteredDashboardStepIdentifier}
                        counts={stepCounts}
                        groups={stepGroups}
                        onChange={handleFilterChange}
                        serviceCode={service.code as OutclinicDashboardServiceCode}
                    />
                )}

                <OutclinicDashboardTable
                    data={processes}
                    episodeCategoryName={service.episodeCategory.name}
                    groups={stepGroups}
                    hideSnoozed={hideSnoozed}
                    isLoading={isLoading}
                    onHideSnoozedChange={() => setHideSnoozed(!hideSnoozed)}
                    onPaginationChange={async (newPage, newItemsPerPage) => {
                        setPageSize(newItemsPerPage);
                        setPage(newPage);
                    }}
                    onWarningsOnlyChange={() => setWarningsOnly(!warningsOnly)}
                    page={page}
                    pageSize={pageSize}
                    serviceCode={service.code as OutclinicDashboardServiceCode}
                    setSnoozeStatus={setSnoozeStatus}
                    totalItems={totalItems}
                    warningsOnly={warningsOnly}
                />
            </Box>
        </>
    );

    function handleFilterChange(dashboardStepIdentifier: string) {
        if (dashboardStepIdentifier === filteredDashboardStepIdentifier) {
            setFilteredDashboardStepIdentifier(null);
        } else {
            setFilteredDashboardStepIdentifier(dashboardStepIdentifier);
        }
    }

    function findDashboardNameByStepIdentifier(dashboardStepIdentifier: string | null) {
        if (!dashboardStepIdentifier) {
            return;
        }
        const steps = stepGroups.flatMap((group) => group.steps);

        return steps.find((step) => step.dashboardStepIdentifier === dashboardStepIdentifier)?.dashboardName;
    }

    async function loadService() {
        const result = await findByCode(props.serviceCode, abortSignal);
        setService(result);
    }

    async function loadStepGroups() {
        if (service) {
            const result = await getStepGroups(service.id, abortSignal);
            setStepGroups(result.items);
        }
    }

    async function loadStepCounts() {
        if (service) {
            const result = await getStepCounts(service.id, { hideSnoozed }, abortSignal);
            setStepCounts(result.items);
        }
    }

    async function loadProcesses() {
        if (service && stepCounts.length > 0) {
            setIsLoading(true);
            const queryParams = {
                filteredDashboardStepIdentifier,
                hideSnoozed,
                limit: pageSize,
                page,
                warningsOnly,
            };
            const result = await listProcesses(service.id, queryParams, abortSignal);
            setProcesses(result.items);
            setTotalItems(result.total);
            setIsLoading(false);
        }
    }

    async function setSnoozeStatus(processId: string, isSnoozed: boolean, snoozedUntilDateTime?: string) {
        if (service) {
            await snoozeProcess(
                {
                    isSnoozed,
                    processId,
                    serviceId: service.id,
                    snoozedUntilDateTime,
                },
                abortSignal
            );

            await Promise.all([loadProcesses(), loadStepCounts()]);
        }
    }
}
